'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Calendar, ChevronLeft, ChevronRight, Images, X } from 'lucide-react';
import { db } from '@/firebase/config';
import { getCityMapConfig, getDiasSS, getGalleryEvents, getProgramaSSConfig } from '@/lib/actions';
import { cn, sanitizeFirestoreData } from '@/lib/utils';
import { ordenarDiasSS } from '@/lib/programa-ss';
import { AVISO_PROGRAMA_SS, ProgramaSSBook } from '@/components/programa-ss/ProgramaSSBook';
import Image from '@/components/ui/Image';
import type { Banda, DiaSS, GalleryEventDoc, JartinetHermandad, ProgramaSSConfig } from '@/lib/types';

/**
 * Elige el evento de la galería que corresponde a la Semana Santa 2026: primero
 * uno que diga "semana santa" y "2026" (en título o fecha), luego cualquiera de
 * "semana santa", y si no, el más reciente. `getGalleryEvents` ya los da
 * ordenados por `order` descendente.
 */
function eventoSemanaSanta(eventos: GalleryEventDoc[]): GalleryEventDoc | null {
  const conFotos = eventos.filter((e) => (e.images?.length || 0) > 0);
  const texto = (e: GalleryEventDoc) => `${e.title} ${e.date}`.toLowerCase();
  return (
    conFotos.find((e) => texto(e).includes('semana santa') && texto(e).includes('2026')) ||
    conFotos.find((e) => texto(e).includes('semana santa')) ||
    conFotos[0] ||
    null
  );
}

/**
 * "Así fue la Semana Santa 2026 de Jartiland" — en la portada, bajo el hero.
 * A la izquierda, las fotos del evento de la galería; a la derecha, el MISMO
 * libro del Programa que hay dentro de Jartinet. Al abrir el libro, este pasa a
 * ocupar todo el ancho (con media columna las carillas no se leen) y la galería
 * baja debajo.
 *
 * Aquí no hay sesión, así que las hermandades y las bandas se leen directamente
 * de Firestore (las dos colecciones son de lectura pública) en vez de salir de
 * `jartinetData`.
 */
export function ProgramaSemanaSantaSection() {
  const [days, setDays] = useState<DiaSS[]>([]);
  const [config, setConfig] = useState<ProgramaSSConfig | null>(null);
  const [cityMapUrl, setCityMapUrl] = useState<string | null>(null);
  const [hermandades, setHermandades] = useState<JartinetHermandad[]>([]);
  const [bandas, setBandas] = useState<Banda[]>([]);
  const [galeria, setGaleria] = useState<GalleryEventDoc | null>(null);
  const [cargando, setCargando] = useState(true);
  const [hayPrograma, setHayPrograma] = useState(true);
  const [libroAbierto, setLibroAbierto] = useState(false);
  const [foto, setFoto] = useState<number | null>(null);

  useEffect(() => {
    let cancelado = false;

    // La galería va aparte: si falla el programa, las fotos siguen saliendo.
    getGalleryEvents().then((evs) => {
      if (!cancelado) setGaleria(eventoSemanaSanta(evs));
    });

    (async () => {
      const [diasResult, configResult, cityMapResult, hermandadesSnap, bandasSnap] = await Promise.all([
        getDiasSS(),
        getProgramaSSConfig(),
        getCityMapConfig(),
        getDocs(collection(db, 'jartinet_hermandades')),
        getDocs(collection(db, 'bandas')),
      ]);
      if (cancelado) return;

      const dias = ordenarDiasSS(diasResult.dias || []);
      setDays(dias);
      setHayPrograma(dias.length > 0);
      setConfig(configResult.config);
      setCityMapUrl(cityMapResult.config?.mapImageUrl || null);
      setHermandades(hermandadesSnap.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as JartinetHermandad[]);
      setBandas(bandasSnap.docs.map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() })) as Banda[]);
      setCargando(false);
    })().catch(() => {
      // Si el programa no se puede cargar, la portada sigue funcionando: el
      // bloque del libro simplemente no se pinta.
      if (!cancelado) { setHayPrograma(false); setCargando(false); }
    });

    return () => { cancelado = true; };
  }, []);

  const mostrarLibro = cargando || hayPrograma;
  const imagenes = galeria?.images || [];
  if (!mostrarLibro && !galeria) return null;

  const dosColumnas = mostrarLibro && !!galeria && !libroAbierto;
  const cerrarFoto = () => setFoto(null);
  const anterior = () => setFoto((i) => (i === null ? null : (i - 1 + imagenes.length) % imagenes.length));
  const siguiente = () => setFoto((i) => (i === null ? null : (i + 1) % imagenes.length));

  return (
    <section id="semana-santa-2026" className="relative w-full scroll-mt-16 py-20 md:py-28">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.35em] text-primary">
            Jartiland 11
          </span>
          <h2 className="mt-3 font-headline text-4xl font-bold md:text-5xl">
            Así fue la Semana Santa 2026 de Jartiland
          </h2>
        </motion.div>

        <div className={cn('mt-12 grid grid-cols-1 items-start gap-8', dosColumnas && 'lg:grid-cols-2')}>
          {/* ── Galería ── */}
          {galeria && (
            <div className={cn('relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-5 backdrop-blur-sm', libroAbierto && 'order-2')}>
              <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500" />
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-headline text-xl font-bold">
                    <Images className="h-5 w-5 text-primary" /> {galeria.title}
                  </p>
                  <p className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                    {galeria.date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {galeria.date}</span>}
                    <span>{imagenes.length} fotos</span>
                  </p>
                </div>
                <Link to="/gallery" className="rounded-xl border border-primary/40 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10">
                  Ver galería completa
                </Link>
              </div>
              <div className={cn('grid grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-3', !libroAbierto && 'max-h-[40rem]')}>
                {imagenes.map((img, i) => (
                  <button
                    key={img.id || `${galeria.id}-${i}`}
                    onClick={() => setFoto(i)}
                    className="group relative aspect-square overflow-hidden rounded-xl border border-white/8 transition-colors hover:border-primary/40"
                  >
                    <Image
                      thumb
                      src={img.src}
                      alt={img.alt || `${galeria.title} — foto ${i + 1}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Libro del Programa ── */}
          {mostrarLibro && (
            <div className={cn('relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-5 backdrop-blur-sm', libroAbierto && 'order-1')}>
              <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500" />
              <p className="flex items-center gap-2 font-headline text-xl font-bold">
                <BookOpen className="h-5 w-5 text-primary" /> Programa de Semana Santa 2026
              </p>
              <p className="mt-1 text-xs italic text-amber-500/80">{AVISO_PROGRAMA_SS}</p>
              <div className="mt-2">
                <ProgramaSSBook
                  days={days}
                  config={config}
                  cityMapUrl={cityMapUrl}
                  allHermandades={hermandades}
                  allBandas={bandas}
                  isLoading={cargando}
                  onOpenChange={setLibroAbierto}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Visor de fotos */}
      <AnimatePresence>
        {foto !== null && imagenes[foto] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={cerrarFoto}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 p-4 backdrop-blur-md"
          >
            <button onClick={cerrarFoto} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/60 hover:text-primary">
              <X className="h-5 w-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); anterior(); }} className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-card/60 hover:text-primary">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); siguiente(); }} className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-card/60 hover:text-primary">
              <ChevronRight className="h-5 w-5" />
            </button>
            <div onClick={(e) => e.stopPropagation()} className="max-h-full max-w-5xl overflow-hidden rounded-2xl border border-white/10">
              <Image src={imagenes[foto].src} alt={imagenes[foto].alt || galeria?.title || ''} className="max-h-[85vh] max-w-full object-contain" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
